import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Button } from '@shared/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select';
import { useToast } from '@shared/hooks/use-toast';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAddRateItem } from '../../hooks/useAddRateItem';
import { CreateRateItemRequest, RateItem, RateItemFormRef, RateItemType } from '../../models/rate.types';
import { checkShippingDuplicate } from '../../utils/shippingDuplicateCheck';
import { translateRateError } from '../../utils/rateErrorTranslator';
import { AddFulfillmentItemForm } from './AddFulfillmentItemForm';
import { AddPackagingItemForm } from './AddPackagingItemForm';
import { AddShippingItemForm } from './AddShippingItemForm';
import { AddStorageItemForm } from './AddStorageItemForm';

export interface AddRateItemModalRef {
  open: () => void;
  openEdit: (item: RateItem) => void;
}

interface AddRateItemModalProps {
  rateId: number;
  existingItems: RateItem[];
  onSuccess?: () => void;
  onItemAdded?: (item: CreateRateItemRequest) => void; // For creation mode
  onItemEdited?: (itemId: number | string, item: CreateRateItemRequest) => void; // For edit mode
}

export const AddRateItemModal = forwardRef<AddRateItemModalRef, AddRateItemModalProps>(
  ({ rateId, existingItems, onSuccess, onItemAdded, onItemEdited }, ref) => {
    const { t } = useTranslation('ratesModule');
    const { toast } = useToast();
    const modalRef = useRef<ModalCenterRef>(null);
    // Un solo ref estable — nunca se reemplaza, solo se limpia/reasigna por useImperativeHandle
    const formRef = useRef<RateItemFormRef | null>(null);

    const [selectedType, setSelectedType] = useState<RateItemType>('shipping');
    const [editingItem, setEditingItem] = useState<RateItem | null>(null);
    const isEditing = editingItem !== null;

    const { mutateAsync: addItem, isPending } = useAddRateItem();

    const hasFulfillment = existingItems.some((item) => item.type === 'fulfillment');
    const hasStorage = existingItems.some((item) => item.type === 'storage');
    const isLocalCreateMode = !!(onItemAdded || onItemEdited);
    const hasPackaging = existingItems.some((item) => item.type === 'packaging');
    const packagingSelectDisabled =
      isLocalCreateMode && hasPackaging && !(isEditing && editingItem?.type === 'packaging');

    useImperativeHandle(ref, () => ({
      open: () => {
        setEditingItem(null);
        setSelectedType('shipping');
        modalRef.current?.openModal();
      },
      openEdit: (item: RateItem) => {
        setEditingItem(item);
        setSelectedType(item.type);
        modalRef.current?.openModal();
      },
    }));

    const handleTypeChange = (type: RateItemType) => {
      setSelectedType(type);
    };

    const handleSubmit = async () => {
      try {
        const formInstance = formRef.current;

        if (!formInstance) {
          toast({
            title: t('validation.incompleteForm'),
            variant: 'destructive',
          });
          return;
        }

        const data = await formInstance.getData();

        if (!data) {
          toast({
            title: t('validation.incompleteForm'),
            description: t('validation.incompleteItemsDescription'),
            variant: 'destructive',
          });
          return;
        }

        if (selectedType === 'shipping') {
          // Al editar, excluir el item actual de la lista para no detectarse a sí mismo como duplicado
          const itemsToCheck = isEditing ? existingItems.filter((item) => item.id !== editingItem.id) : existingItems;
          const duplicateResult = checkShippingDuplicate(data, itemsToCheck);
          if (duplicateResult.hasDuplicate) {
            toast({
              title: t('validation.duplicateShipping', {
                itemTitle: duplicateResult.conflictingItemTitle,
              }),
              variant: 'destructive',
            });
            return;
          }
        }

        // If in creation/edit mode (local items)
        if (onItemAdded || onItemEdited) {
          // Usar nombres resueltos por el formulario (si los tiene)
          const resolvedNames = (data as any)._resolvedNames || {};
          const cleanData = { ...(data as Record<string, unknown>) };
          delete cleanData._resolvedNames;

          const itemWithNames: CreateRateItemRequest & {
            courier_name?: string | null;
            courier_service_name?: string | null;
            country_name?: string | null;
            courier_zone_name?: string | null;
          } = {
            ...cleanData,
            courier_name: resolvedNames.courier_name || null,
            courier_service_name: resolvedNames.courier_service_name || null,
            country_name: resolvedNames.country_name || null,
            courier_zone_name: resolvedNames.courier_zone_name || null,
          };

          if (isEditing && onItemEdited) {
            onItemEdited(editingItem.id, itemWithNames);
          } else if (onItemAdded) {
            onItemAdded(itemWithNames);
          }

          setEditingItem(null);
          modalRef.current?.closeModal();
          return;
        }

        // Otherwise, save to API (edit mode)
        await addItem({ rateId, data });

        toast({
          title: t('toast.addItemSuccess'),
        });

        modalRef.current?.closeModal();
        onSuccess?.();
      } catch (error: any) {
        toast({
          title: t('toast.addItemError'),
          description: translateRateError(error, t),
          variant: 'destructive',
        });
      }
    };

    const renderForm = () => {
      const initialData = editingItem || undefined;
      switch (selectedType) {
        case 'shipping':
          return (
            <AddShippingItemForm
              key={`${selectedType}-${editingItem?.id || 'new'}`}
              ref={formRef}
              initialData={initialData}
            />
          );
        case 'fulfillment':
          return (
            <AddFulfillmentItemForm
              key={`${selectedType}-${editingItem?.id || 'new'}`}
              ref={formRef}
              initialData={initialData}
            />
          );
        case 'storage':
          return (
            <AddStorageItemForm
              key={`${selectedType}-${editingItem?.id || 'new'}`}
              ref={formRef}
              initialData={initialData}
            />
          );
        case 'packaging':
          return (
            <AddPackagingItemForm
              key={`${selectedType}-${editingItem?.id || 'new'}`}
              ref={formRef}
              initialData={initialData}
              existingItems={existingItems}
            />
          );
        default:
          return null;
      }
    };

    return (
      <ModalCenter
        ref={modalRef}
        title={isEditing ? t('modal.editItem.title') : t('modal.addItem.title')}
        type="default"
        width={selectedType === 'packaging' ? 'xl' : 'lg'}
        classNameBody="p-0 flex flex-col max-h-[calc(100vh-200px)]"
      >
        <div className="flex flex-col h-full">
          {/* Contenido con scroll */}
          <div className="flex-1 overflow-y-auto p-4 space-y-6">
            {!isEditing && (
              <div>
                <p className="text-sm text-subtext mb-2">{t('modal.addItem.selectType')}</p>
                <Select value={selectedType} onValueChange={(v) => handleTypeChange(v as RateItemType)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="shipping">{t('itemTypes.shipping')}</SelectItem>
                    <SelectItem value="fulfillment" disabled={hasFulfillment}>
                      {t('itemTypes.fulfillment')}
                      {hasFulfillment && ` (${t('modal.addItem.alreadyExists')})`}
                    </SelectItem>
                    <SelectItem value="storage" disabled={hasStorage}>
                      {t('itemTypes.storage')}
                      {hasStorage && ` (${t('modal.addItem.alreadyExists')})`}
                    </SelectItem>
                    <SelectItem value="packaging" disabled={packagingSelectDisabled}>
                      {t('itemTypes.packaging')}
                      {packagingSelectDisabled && ` (${t('modal.addItem.alreadyExists')})`}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="border-t pt-4">{renderForm()}</div>
          </div>

          {/* Botones fijos en la parte inferior */}
          <div className="flex justify-end gap-3 p-4 border-t border-card-border bg-body-bg">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setEditingItem(null);
                modalRef.current?.closeModal();
              }}
              disabled={isPending}
            >
              {t('modal.addItem.cancel')}
            </Button>
            <Button type="button" variant="primary" className="!text-white" onClick={handleSubmit} disabled={isPending}>
              {isPending
                ? t('modal.addItem.adding')
                : isEditing
                  ? t('modal.editItem.submit')
                  : t('modal.addItem.submit')}
            </Button>
          </div>
        </div>
      </ModalCenter>
    );
  },
);

AddRateItemModal.displayName = 'AddRateItemModal';
